<template>
  <div class="page-container">
    <div class="bg-grid"></div>
    <div class="bg-glow"></div>

    <header class="page-header">
      <div class="breadcrumb">
        <router-link to="/" class="breadcrumb-link">{{ $i18n.locale === 'zh' ? '首页' : 'Home' }}</router-link>
        <span class="breadcrumb-sep">/</span>
        <span class="breadcrumb-current">{{ $i18n.t('pages.password.title') }}</span>
      </div>
      <div class="header-content">
        <div class="header-icon">
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
            <path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4"/>
          </svg>
        </div>
        <h1 class="page-title">{{ $i18n.t('pages.password.title') }}</h1>
        <p class="page-subtitle">{{ $i18n.t('pages.password.subtitle') }}</p>
      </div>
    </header>

    <main class="page-content">
      <div class="generator-card">
        <div class="password-display">
          <input type="text" v-model="generatedPassword" readonly class="password-input" />
          <button @click="copyPassword" class="copy-btn">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/>
              <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
            </svg>
          </button>
          <button @click="generatePassword" class="refresh-btn">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M23 4v6h-6"/><path d="M1 20v-6h6"/>
              <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/>
            </svg>
          </button>
        </div>

        <div class="options-section">
          <div class="option-group">
            <label class="option-label">
              <span>{{ $i18n.t('pages.password.length') }}</span>
              <span class="length-value">{{ passwordLength }}</span>
            </label>
            <input type="range" v-model.number="passwordLength" min="8" max="64" class="range-slider" />
            <div class="range-marks">
              <span>8</span>
              <span>16</span>
              <span>24</span>
              <span>32</span>
              <span>40</span>
              <span>48</span>
              <span>56</span>
              <span>64</span>
            </div>
          </div>

          <div class="checkbox-group">
            <label class="checkbox-item" :class="{ active: includeUppercase }">
              <input type="checkbox" v-model="includeUppercase" />
              <span class="checkbox-icon">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3">
                  <polyline points="20 6 9 17 4 12"/>
                </svg>
              </span>
              <span class="checkbox-text">{{ $i18n.t('pages.password.uppercase') }} <span class="char-sample">A-Z</span></span>
            </label>

            <label class="checkbox-item" :class="{ active: includeLowercase }">
              <input type="checkbox" v-model="includeLowercase" />
              <span class="checkbox-icon">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3">
                  <polyline points="20 6 9 17 4 12"/>
                </svg>
              </span>
              <span class="checkbox-text">{{ $i18n.t('pages.password.lowercase') }} <span class="char-sample">a-z</span></span>
            </label>

            <label class="checkbox-item" :class="{ active: includeNumbers }">
              <input type="checkbox" v-model="includeNumbers" />
              <span class="checkbox-icon">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3">
                  <polyline points="20 6 9 17 4 12"/>
                </svg>
              </span>
              <span class="checkbox-text">{{ $i18n.t('pages.password.numbers') }} <span class="char-sample">0-9</span></span>
            </label>

            <label class="checkbox-item" :class="{ active: includeSpecialChars }">
              <input type="checkbox" v-model="includeSpecialChars" />
              <span class="checkbox-icon">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3">
                  <polyline points="20 6 9 17 4 12"/>
                </svg>
              </span>
              <span class="checkbox-text">{{ $i18n.t('pages.password.special') }} <span class="char-sample">!@#$%</span></span>
            </label>
          </div>
        </div>

        <button @click="generatePassword" class="generate-btn">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M23 4v6h-6"/><path d="M1 20v-6h6"/>
            <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/>
          </svg>
          {{ $i18n.t('pages.password.generate') }}
        </button>
      </div>
    </main>
  </div>
</template>

<script>
export default {
  name: 'PasswordGeneratorPage',
  inject: ['i18n'],
  data() {
    return {
      generatedPassword: '',
      passwordLength: 16,
      includeUppercase: true,
      includeLowercase: true,
      includeNumbers: true,
      includeSpecialChars: true
    }
  },
  methods: {
    generatePassword() {
      if (!this.includeUppercase && !this.includeLowercase && !this.includeNumbers && !this.includeSpecialChars) {
        return
      }

      let charset = ''
      const upper = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'
      const lower = 'abcdefghijklmnopqrstuvwxyz'
      const nums = '0123456789'
      const special = '!@#$%^&*()_+~`|}{[]:;?><,./-='

      if (this.includeUppercase) {
        charset += upper
      }
      if (this.includeLowercase) {
        charset += lower
      }
      if (this.includeNumbers) {
        charset += nums
      }
      if (this.includeSpecialChars) {
        charset += special
      }

      let password = ''
      for (let i = 0; i < this.passwordLength; i++) {
        password += charset[Math.floor(Math.random() * charset.length)]
      }
      this.generatedPassword = password
    },
    copyPassword() {
      if (!this.generatedPassword) {
        return
      }
      navigator.clipboard.writeText(this.generatedPassword)
    }
  },
  mounted() {
    this.generatePassword()
  }
}
</script>

<style scoped>
.page-container {
  min-height: 100vh;
  padding: 40px 24px 60px;
  position: relative;
  overflow: hidden;
}

.bg-grid {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-image:
    linear-gradient(rgba(232, 255, 0, 0.03) 1px, transparent 1px),
    linear-gradient(90deg, rgba(232, 255, 0, 0.03) 1px, transparent 1px);
  background-size: 60px 60px;
  pointer-events: none;
  z-index: 0;
}

.bg-glow {
  position: fixed;
  width: 500px;
  height: 500px;
  background: radial-gradient(circle, rgba(0, 240, 255, 0.08) 0%, transparent 70%);
  bottom: -200px;
  left: 50%;
  transform: translateX(-50%);
  filter: blur(100px);
  pointer-events: none;
  z-index: 0;
}

.page-header {
  max-width: 600px;
  margin: 0 auto 50px;
  position: relative;
  z-index: 1;
}

.breadcrumb {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 30px;
  font-family: var(--font-mono);
  font-size: 12px;
}

.breadcrumb-link {
  color: var(--text-secondary);
  text-decoration: none;
  transition: color 0.3s;
}

.breadcrumb-link:hover {
  color: var(--accent-primary);
}

.breadcrumb-sep {
  color: var(--border-color);
}

.breadcrumb-current {
  color: var(--accent-primary);
}

.header-content {
  text-align: center;
}

.header-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 64px;
  height: 64px;
  background: var(--bg-card);
  border: 1px solid var(--border-color);
  border-radius: 16px;
  color: var(--accent-primary);
  margin-bottom: 20px;
}

.page-title {
  font-family: var(--font-display);
  font-size: 48px;
  letter-spacing: 2px;
  color: var(--text-primary);
  margin-bottom: 12px;
}

.page-subtitle {
  font-size: 16px;
  color: var(--text-secondary);
  font-weight: 300;
}

.page-content {
  max-width: 600px;
  margin: 0 auto;
  position: relative;
  z-index: 1;
}

.generator-card {
  background: var(--bg-card);
  border: 1px solid var(--border-color);
  border-radius: 20px;
  padding: 40px;
}

.password-display {
  display: flex;
  gap: 12px;
  margin-bottom: 40px;
}

.password-input {
  flex: 1;
  padding: 20px 24px;
  background: var(--bg-secondary);
  border: 1px solid var(--border-color);
  border-radius: 14px;
  color: var(--accent-primary);
  font-family: var(--font-mono);
  font-size: 18px;
  letter-spacing: 1px;
  text-align: center;
}

.password-input:focus {
  outline: none;
  border-color: var(--accent-primary);
}

.copy-btn,
.refresh-btn {
  width: 56px;
  height: 56px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--bg-secondary);
  border: 1px solid var(--border-color);
  border-radius: 14px;
  color: var(--text-secondary);
  cursor: pointer;
  transition: all 0.3s ease;
}

.copy-btn:hover,
.refresh-btn:hover {
  border-color: var(--accent-primary);
  color: var(--accent-primary);
}

.options-section {
  margin-bottom: 32px;
}

.option-group {
  margin-bottom: 32px;
}

.option-label {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  font-size: 14px;
  color: var(--text-secondary);
}

.length-value {
  font-family: var(--font-mono);
  font-size: 24px;
  font-weight: 700;
  color: var(--accent-primary);
}

.range-slider {
  width: 100%;
  height: 8px;
  background: var(--bg-secondary);
  border-radius: 4px;
  outline: none;
  -webkit-appearance: none;
}

.range-slider::-webkit-slider-thumb {
  -webkit-appearance: none;
  width: 20px;
  height: 20px;
  background: var(--accent-primary);
  border-radius: 50%;
  cursor: pointer;
  box-shadow: 0 2px 8px rgba(232, 255, 0, 0.4);
}

.range-marks {
  display: flex;
  justify-content: space-between;
  margin-top: 8px;
  font-family: var(--font-mono);
  font-size: 10px;
  color: var(--text-secondary);
}

.checkbox-group {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

.checkbox-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  background: var(--bg-secondary);
  border: 1px solid var(--border-color);
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.3s ease;
}

.checkbox-item input {
  display: none;
}

.checkbox-icon {
  width: 22px;
  height: 22px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--bg-primary);
  border: 1px solid var(--border-color);
  border-radius: 6px;
  color: transparent;
  transition: all 0.3s ease;
}

.checkbox-item.active .checkbox-icon {
  background: var(--accent-primary);
  border-color: var(--accent-primary);
  color: var(--bg-primary);
}

.checkbox-item.active {
  border-color: var(--accent-primary);
}

.checkbox-text {
  font-size: 14px;
  color: var(--text-primary);
}

.char-sample {
  color: var(--text-secondary);
  font-family: var(--font-mono);
  font-size: 12px;
}

.generate-btn {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 18px 24px;
  background: var(--accent-primary);
  color: var(--bg-primary);
  border: none;
  border-radius: 14px;
  font-family: var(--font-body);
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
}

.generate-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 24px rgba(232, 255, 0, 0.3);
}

@media (max-width: 640px) {
  .page-container {
    padding: 24px 16px 40px;
  }

  .page-title {
    font-size: 36px;
  }

  .generator-card {
    padding: 24px;
  }

  .password-display {
    flex-direction: column;
  }

  .copy-btn,
  .refresh-btn {
    width: 100%;
    height: 48px;
  }

  .checkbox-group {
    grid-template-columns: 1fr;
  }
}
</style>
